import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import AdminShell from "../AdminShell";
import { adminFetch } from "../adminApi";
import { useAdminToast } from "../AdminToast";
import ConfirmModal from "../ConfirmModal";
import { formatSpecialDay } from "../useBusinessInfo";
import "./admin-settings.css";

/**
 * RAdminSettings — /admin/settings
 *
 * Redesigned System Settings screen (design-ref/admin/Settings.html) with
 * functionality replicated 1:1 from the legacy
 * frontend/src/pages/AdminSettingsPage.js:
 *
 *   - GET /api/admin/settings on mount (auth via adminFetch — missing/expired
 *     token or 401 clears the token and redirects to /admin/login)
 *   - 7 tabs, EACH with its own Save button + endpoint (no global save):
 *       Shipping  → PUT /api/admin/settings/shipping        (settings.shipping)
 *       Email     → PUT /api/admin/settings/email           (settings.email)
 *       Hours     → PUT /api/admin/settings/opening-hours   (settings.opening_hours:
 *                   closed_on_tuesdays, holiday_hours_notice, weekly[7] open/close
 *                   times, special_days[]); preview = GET /api/opening-hours
 *       Tours     → PUT /api/admin/settings/tour-pricing
 *                   ({ tour_pricing, tour_schedule || {restrict_monday_friday:true} })
 *                   + Discount Coupons card → GET/POST/DELETE /api/admin/tour-coupons
 *                     (saves immediately, not via the Tours Save button)
 *       Stripe    → PUT /api/admin/settings/stripe          (settings.stripe)
 *       Features  → PUT /api/admin/settings/features        (settings.features)
 *       General   → PUT /api/admin/settings                 (whole settings object)
 *   - legacy success/error banners (3s/5s auto-clear) → toast pills, with the
 *     legacy message strings preserved verbatim
 *   - Email testing: basic test POST /api/admin/settings/email/test?test_email=…
 *     (editable target, default hello@turbo360.com.au) + 4 template tests
 *     POST /api/admin/settings/email/test/{order-confirmation|admin-notification|
 *     shipping-notification|tour-confirmation} — all hardcoded to
 *     hello@turbo360.com.au server-side; testingEmail disables every test button
 *   - Admin notification emails: per-row inline edit, remove disabled when only
 *     one remains; legacy prompt() add replaced by an inline input row (design
 *     pattern) with the same includes('@') validation
 *   - Tours: adult/child pricing (display fallbacks 16.50/9.50), family +
 *     custom package lists (each with a people count — family 3–8, custom 1–100) (enabled toggle = pkg.enabled !== false, delete,
 *     add-forms with required-field gating), Monday/Friday schedule restriction
 *     (checked = restrict_monday_friday !== false) with start/end dates
 *     ('' shown, null saved when cleared), active-period/no-start-date notes,
 *     and override conditions (add requires start+end, enabled toggle, delete)
 *   - Stripe TEST/LIVE toggle + 3 test keys + 3 live keys (+ live warning);
 *     secrets masked with show/hide eye toggles (design upgrade, same data)
 *   - Features: tour_booking_enabled (!== false) / store_maintenance_mode
 *     (=== true) via styled switches
 *   - General: shop name/url, currency + symbol, GST tax rate (0–1 decimal),
 *     tax_inclusive switch
 *   - legacy parse rules kept exactly: shipping_fee parseFloat||0, threshold
 *     null when blank, smtp_port parseInt||587, adult/child parseFloat,
 *     tax_rate parseFloat||0, package price parseFloat||0, guest parseInt||0
 *   - legacy "Back to Dashboard" button is superseded by the shell sidebar
 */

const TABS = [
  { key: "shipping", label: "Shipping" },
  { key: "email", label: "Email" },
  { key: "hours", label: "Hours" },
  { key: "tours", label: "Tours" },
  { key: "stripe", label: "Stripe" },
  { key: "features", label: "Features" },
  { key: "general", label: "General" },
];

/* Template email tests — endpoints + legacy success copy preserved exactly. */
const TEMPLATE_TESTS = [
  {
    endpoint: "order-confirmation",
    label: "Test: Order Confirmation",
    success: "Test order confirmation email sent to hello@turbo360.com.au!",
  },
  {
    endpoint: "admin-notification",
    label: "Test: Admin Order Alert",
    success: "Test admin notification email sent to hello@turbo360.com.au!",
  },
  {
    endpoint: "shipping-notification",
    label: "Test: Shipping Notification",
    success: "Test shipping notification email sent to hello@turbo360.com.au!",
  },
  {
    endpoint: "tour-confirmation",
    label: "Test: Tour Confirmation",
    success: "Test tour confirmation email sent to hello@turbo360.com.au!",
  },
];

const DEFAULT_SETTINGS = {
  shop_name: "Umoona Opal Mine",
  shop_url: "https://umoonaopalmine.com.au",
  currency: "AUD",
  currency_symbol: "$",
  tax_rate: 0.1,
  tax_inclusive: true,
  shipping: {
    shipping_fee: 10.0,
    free_shipping_threshold: null,
    allowed_countries: ["AU"],
  },
  email: {
    smtp_host: "",
    smtp_port: 587,
    smtp_user: "",
    smtp_password: "",
    sender_email: "",
    sender_name: "Umoona Opal Mine",
    admin_notification_emails: [],
    customer_reply_to: null,
  },
  opening_hours: {
    closed_on_tuesdays: false,
    holiday_hours_notice: false,
    weekly: null, // filled with DEFAULT_WEEKLY when missing
    special_days: [],
  },
  features: {
    tour_booking_enabled: true,
  },
  stripe: {
    use_live_keys: false,
    test_api_key: "",
    test_publishable_key: "",
    test_webhook_secret: "",
    live_api_key: "",
    live_publishable_key: "",
    live_webhook_secret: "",
  },
};

const EMPTY_OVERRIDE = {
  label: "",
  start_date: "",
  end_date: "",
  apply_monday: true,
  apply_friday: true,
  allow_morning: false,
  allow_afternoon: false,
  enabled: true,
};

const EMPTY_COUPON = { code: "", discount_percent: "", description: "" };

/* Opening hours: Monday = day 0 … Sunday = day 6 (matches the backend). */
const WEEKDAY_NAMES = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const DEFAULT_WEEKLY = WEEKDAY_NAMES.map((_, day) => ({
  day,
  closed: false,
  open: "08:30",
  close: "17:30",
}));
const EMPTY_SPECIAL_DAY = { date: "", label: "", closed: true, open: "10:00", close: "15:00" };

const weeklyOf = (oh) =>
  oh && Array.isArray(oh.weekly) && oh.weekly.length === 7 ? oh.weekly : DEFAULT_WEEKLY;

/* Body for PUT /api/admin/settings/opening-hours — Tuesday's row and the
   legacy closed_on_tuesdays flag are kept in step. */
const openingHoursPayload = (oh = {}) => {
  const weekly = weeklyOf(oh).map((d) => ({
    day: d.day,
    closed: !!d.closed,
    open: d.open || "08:30",
    close: d.close || "17:30",
  }));
  return {
    closed_on_tuesdays: weekly[1].closed,
    holiday_hours_notice: !!oh.holiday_hours_notice,
    weekly,
    special_days: (oh.special_days || [])
      .filter((d) => d.date)
      .map((d) => ({
        date: d.date,
        label: (d.label || "").trim(),
        closed: !!d.closed,
        open: d.closed ? null : d.open || null,
        close: d.closed ? null : d.close || null,
      })),
  };
};

const fmtLongDate = (value) =>
  new Date(value).toLocaleDateString("en-AU", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

/* Module-scope so React keeps a stable component identity across renders —
   defining these inside RAdminSettings remounted them (and dropped input
   focus) on every keystroke. */
const SecretField = ({ id, label, value, placeholder, onChange, revealedMap, onToggle }) => (
  <label className="adm-field">
    <span className="adm-label">{label}</span>
    <div className="pgset-secret">
      <input
        className="adm-input pgset-secret-input"
        type={revealedMap[id] ? "text" : "password"}
        value={value}
        placeholder={placeholder}
        onChange={onChange}
        autoComplete="off"
      />
      <button
        type="button"
        className="pgset-eye"
        onClick={() => onToggle(id)}
        aria-label={revealedMap[id] ? "Hide value" : "Show value"}
        title={revealedMap[id] ? "Hide" : "Show"}
      >
        {revealedMap[id] ? <EyeOff size={17} /> : <Eye size={17} />}
      </button>
    </div>
  </label>
);

const SaveRow = ({ section, label, saving, onSave }) => (
  <div className="adm-right">
    <button
      type="button"
      className="adm-btn"
      onClick={() => onSave(section)}
      disabled={saving}
    >
      {saving ? "Saving…" : label}
    </button>
  </div>
);

const CardHead = ({ title }) => (
  <div className="adm-card-head">
    <div className="adm-card-title adm-card-title--lg">{title}</div>
    <span className="adm-card-note">Saves independently</span>
  </div>
);

const RAdminSettings = () => {
  const navigate = useNavigate();
  const toast = useAdminToast();

  const [activeTab, setActiveTab] = useState("shipping");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [testEmail, setTestEmail] = useState("hello@turbo360.com.au");

  const [settings, setSettings] = useState(DEFAULT_SETTINGS);

  // Custom package add-form (legacy: newPackage / showAddPackage)
  const [newPackage, setNewPackage] = useState({
    name: "",
    price: "",
    description: "",
    guest_count: "",
    enabled: true,
  });
  const [showAddPackage, setShowAddPackage] = useState(false);

  // Family package add-form (legacy: newFamilyPackage / showAddFamilyPackage)
  const [newFamilyPackage, setNewFamilyPackage] = useState({
    name: "",
    price: "",
    description: "",
    guest_count: "",
    enabled: true,
  });
  const [showAddFamilyPackage, setShowAddFamilyPackage] = useState(false);

  // Override add-form (legacy: newOverride / showAddOverride)
  const [showAddOverride, setShowAddOverride] = useState(false);
  const [newOverride, setNewOverride] = useState(EMPTY_OVERRIDE);

  // Inline add-row for admin notification emails (replaces legacy prompt())
  const [showAddEmail, setShowAddEmail] = useState(false);
  const [newEmailAddress, setNewEmailAddress] = useState("");

  // Show/hide state for masked secret fields (design upgrade)
  const [revealed, setRevealed] = useState({});
  const toggleReveal = (key) =>
    setRevealed((prev) => ({ ...prev, [key]: !prev[key] }));

  // Tour coupons — own endpoints, saved immediately (not part of the Tours Save)
  const [coupons, setCoupons] = useState([]);
  const [couponsLoading, setCouponsLoading] = useState(true);
  const [showAddCoupon, setShowAddCoupon] = useState(false);
  const [newCoupon, setNewCoupon] = useState(EMPTY_COUPON);
  const [couponBusy, setCouponBusy] = useState(false);
  const [deletingCoupon, setDeletingCoupon] = useState(null);

  // What the website currently shows (GET /api/opening-hours) — the Hours
  // tab preview, refreshed after each save.
  const [liveHours, setLiveHours] = useState(null);

  /* ---------------- data ---------------- */

  const fetchLiveHours = useCallback(async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/opening-hours`);
      if (response.ok) setLiveHours(await response.json());
    } catch (err) {
      console.error("Opening hours preview fetch error:", err);
    }
  }, []);

  useEffect(() => {
    fetchLiveHours();
  }, [fetchLiveHours]);

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      const response = await adminFetch("/api/admin/settings", { navigate });
      if (!response.ok) {
        throw new Error("Failed to fetch settings");
      }
      const data = await response.json();
      setSettings(data);
    } catch (err) {
      if (!err.isAuthError) {
        console.error("Settings fetch error:", err);
        toast.error(err.message);
      }
    } finally {
      setLoading(false);
    }
  }, [navigate, toast]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  /* ---------------- tour coupons ---------------- */

  const fetchCoupons = useCallback(async () => {
    try {
      const response = await adminFetch("/api/admin/tour-coupons", { navigate });
      if (!response.ok) {
        throw new Error("Failed to fetch coupons");
      }
      setCoupons(await response.json());
    } catch (err) {
      if (!err.isAuthError) toast.error(err.message);
    } finally {
      setCouponsLoading(false);
    }
  }, [navigate, toast]);

  useEffect(() => {
    fetchCoupons();
  }, [fetchCoupons]);

  const addCoupon = async () => {
    const discount = parseFloat(newCoupon.discount_percent);
    if (!newCoupon.code.trim() || !discount) return;
    try {
      setCouponBusy(true);
      const response = await adminFetch("/api/admin/tour-coupons", {
        method: "POST",
        body: JSON.stringify({
          code: newCoupon.code.trim().toUpperCase(),
          discount_percent: discount,
          description: newCoupon.description.trim() || null,
        }),
        navigate,
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          typeof errorData.detail === "string"
            ? errorData.detail
            : "Failed to add coupon"
        );
      }
      const created = await response.json();
      toast.success(`Coupon ${created.code} added`);
      setNewCoupon(EMPTY_COUPON);
      setShowAddCoupon(false);
      fetchCoupons();
    } catch (err) {
      if (!err.isAuthError) toast.error(err.message);
    } finally {
      setCouponBusy(false);
    }
  };

  const confirmDeleteCoupon = async () => {
    if (!deletingCoupon) return;
    try {
      setCouponBusy(true);
      const response = await adminFetch(
        `/api/admin/tour-coupons/${deletingCoupon.id}`,
        { method: "DELETE", navigate }
      );
      if (!response.ok) {
        throw new Error("Failed to delete coupon");
      }
      toast.success(`Coupon ${deletingCoupon.code} deleted`);
      setCoupons((prev) => prev.filter((c) => c.id !== deletingCoupon.id));
      setDeletingCoupon(null);
    } catch (err) {
      if (!err.isAuthError) toast.error(err.message);
    } finally {
      setCouponBusy(false);
    }
  };

  /* Section saves — url/payload mapping copied exactly from the legacy page. */
  const updateSettings = async (section = null) => {
    try {
      setSaving(true);

      let url = "/api/admin/settings";
      let payload = settings;

      if (section === "shipping") {
        url += "/shipping";
        payload = settings.shipping;
      } else if (section === "email") {
        url += "/email";
        payload = settings.email;
      } else if (section === "opening_hours") {
        url += "/opening-hours";
        payload = openingHoursPayload(settings.opening_hours);
      } else if (section === "features") {
        url += "/features";
        payload = settings.features;
      } else if (section === "stripe") {
        url += "/stripe";
        payload = settings.stripe;
      } else if (section === "tour_pricing") {
        url += "/tour-pricing";
        // Include both tour_pricing and tour_schedule when saving tours settings
        payload = {
          tour_pricing: settings.tour_pricing,
          tour_schedule: settings.tour_schedule || {
            restrict_monday_friday: true,
          },
        };
      }

      const response = await adminFetch(url, {
        method: "PUT",
        body: JSON.stringify(payload),
        navigate,
      });

      if (!response.ok) {
        // Validation errors (e.g. bad hours or package sizes) come back as a
        // plain-English 400 detail — show that instead of the generic message.
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          typeof errorData.detail === "string"
            ? errorData.detail
            : "Failed to update settings"
        );
      }

      if (section === "opening_hours") fetchLiveHours();

      // Legacy success copy preserved verbatim (incl. the raw section token).
      toast.success(
        `${
          section
            ? section.charAt(0).toUpperCase() + section.slice(1)
            : "Settings"
        } updated successfully!`,
        3000
      );
    } catch (err) {
      if (!err.isAuthError) toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  /* ---------------- email tests ---------------- */

  const testEmailSettings = async () => {
    try {
      setTestingEmail(true);
      const response = await adminFetch(
        `/api/admin/settings/email/test?test_email=${encodeURIComponent(
          testEmail
        )}`,
        { method: "POST", navigate }
      );
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to send test email");
      }
      toast.success(`Test email sent successfully to ${testEmail}!`, 5000);
    } catch (err) {
      if (!err.isAuthError) toast.error(err.message);
    } finally {
      setTestingEmail(false);
    }
  };

  const runTemplateTest = async (endpoint, successMessage) => {
    try {
      setTestingEmail(true);
      const response = await adminFetch(
        `/api/admin/settings/email/test/${endpoint}`,
        { method: "POST", navigate }
      );
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to send test email");
      }
      toast.success(successMessage, 5000);
    } catch (err) {
      if (!err.isAuthError) toast.error(err.message);
    } finally {
      setTestingEmail(false);
    }
  };

  /* ---------------- state helpers (legacy handleInputChange semantics) ----- */

  const handleInputChange = (section, field, value) => {
    if (section) {
      setSettings((prev) => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value,
        },
      }));
    } else {
      setSettings((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  /* --- admin notification emails --- */

  const updateEmailAt = (index, value) => {
    const newEmails = [
      ...((settings.email && settings.email.admin_notification_emails) || []),
    ];
    newEmails[index] = value;
    handleInputChange("email", "admin_notification_emails", newEmails);
  };

  const removeEmailAddress = (index) => {
    setSettings((prev) => ({
      ...prev,
      email: {
        ...prev.email,
        admin_notification_emails: prev.email.admin_notification_emails.filter(
          (_, i) => i !== index
        ),
      },
    }));
  };

  // Legacy prompt() add, re-expressed as an inline row — same '@' validation.
  const confirmAddEmail = () => {
    const newEmail = newEmailAddress;
    if (newEmail && newEmail.includes("@")) {
      setSettings((prev) => ({
        ...prev,
        email: {
          ...prev.email,
          admin_notification_emails: [
            ...prev.email.admin_notification_emails,
            newEmail,
          ],
        },
      }));
      setNewEmailAddress("");
      setShowAddEmail(false);
    }
  };

  /* --- tour packages --- */

  const updatePackageField = (kind, index, field, value) => {
    setSettings((prev) => {
      const list = [
        ...((prev.tour_pricing && prev.tour_pricing[kind]) || []),
      ];
      list[index] = { ...list[index], [field]: value };
      return {
        ...prev,
        tour_pricing: { ...prev.tour_pricing, [kind]: list },
      };
    });
  };

  const removePackage = (kind, index) => {
    const updated = (
      (settings.tour_pricing && settings.tour_pricing[kind]) ||
      []
    ).filter((_, i) => i !== index);
    handleInputChange("tour_pricing", kind, updated);
  };

  const addFamilyPackage = () => {
    if (
      newFamilyPackage.name &&
      newFamilyPackage.price &&
      newFamilyPackage.description &&
      newFamilyPackage.guest_count >= 3 &&
      newFamilyPackage.guest_count <= 8
    ) {
      const updated = [
        ...((settings.tour_pricing && settings.tour_pricing.family_packages) ||
          []),
        newFamilyPackage,
      ];
      handleInputChange("tour_pricing", "family_packages", updated);
      setNewFamilyPackage({
        name: "",
        price: "",
        description: "",
        guest_count: "",
        enabled: true,
      });
      setShowAddFamilyPackage(false);
    }
  };

  const addCustomPackage = () => {
    if (
      newPackage.name &&
      newPackage.price &&
      newPackage.description &&
      newPackage.guest_count >= 1 &&
      newPackage.guest_count <= 100
    ) {
      const updated = [
        ...((settings.tour_pricing && settings.tour_pricing.custom_packages) ||
          []),
        newPackage,
      ];
      handleInputChange("tour_pricing", "custom_packages", updated);
      setNewPackage({ name: "", price: "", description: "", guest_count: "", enabled: true });
      setShowAddPackage(false);
    }
  };

  /* --- schedule overrides --- */

  const updateOverrideField = (index, field, value) => {
    setSettings((prev) => {
      const overrides = [
        ...((prev.tour_schedule && prev.tour_schedule.overrides) || []),
      ];
      overrides[index] = { ...overrides[index], [field]: value };
      return {
        ...prev,
        tour_schedule: { ...prev.tour_schedule, overrides },
      };
    });
  };

  const handleToggleOverride = (index) => {
    const overrides = [
      ...((settings.tour_schedule && settings.tour_schedule.overrides) || []),
    ];
    overrides[index] = {
      ...overrides[index],
      enabled: !overrides[index].enabled,
    };
    setSettings((prev) => ({
      ...prev,
      tour_schedule: { ...prev.tour_schedule, overrides },
    }));
  };

  const handleRemoveOverride = (index) => {
    const overrides = [
      ...((settings.tour_schedule && settings.tour_schedule.overrides) || []),
    ];
    overrides.splice(index, 1);
    setSettings((prev) => ({
      ...prev,
      tour_schedule: { ...prev.tour_schedule, overrides },
    }));
  };

  const handleAddOverride = () => {
    if (!newOverride.start_date || !newOverride.end_date) return;
    const overrides = [
      ...((settings.tour_schedule && settings.tour_schedule.overrides) || []),
      { ...newOverride },
    ];
    setSettings((prev) => ({
      ...prev,
      tour_schedule: { ...prev.tour_schedule, overrides },
    }));
    setNewOverride(EMPTY_OVERRIDE);
    setShowAddOverride(false);
  };

  /* ---------------- render helpers ---------------- */

  const notificationEmails =
    (settings.email && settings.email.admin_notification_emails) || [];
  const restricted =
    settings.tour_schedule?.restrict_monday_friday !== false;
  const useLive = settings.stripe?.use_live_keys || false;

  /* ---------------- tab panels ---------------- */

  const renderShipping = () => (
    <section className="adm-card pgset-card">
      <CardHead title="Shipping Settings" />
      <div className="adm-grid2">
        <label className="adm-field">
          <span className="adm-label">Shipping Fee (AUD)</span>
          <input
            className="adm-input"
            type="number"
            step="0.01"
            value={settings.shipping?.shipping_fee ?? ""}
            onChange={(e) =>
              handleInputChange(
                "shipping",
                "shipping_fee",
                parseFloat(e.target.value) || 0
              )
            }
            placeholder="15.00"
          />
        </label>
        <label className="adm-field">
          <span className="adm-label">Free Shipping Threshold (AUD)</span>
          <input
            className="adm-input"
            type="number"
            step="0.01"
            value={settings.shipping?.free_shipping_threshold || ""}
            onChange={(e) =>
              handleInputChange(
                "shipping",
                "free_shipping_threshold",
                e.target.value ? parseFloat(e.target.value) : null
              )
            }
            placeholder="Leave blank for none"
          />
        </label>
      </div>
      <div className="adm-sec-eyebrow">Allowed countries</div>
      <div className="pgset-countries">
        {(settings.shipping?.allowed_countries || []).map((country, index) => (
          <span key={index} className="pgset-country">
            {country === "AU" ? "🇦🇺 Australia" : country}
          </span>
        ))}
      </div>
      <div className="pgset-hint">
        Shipping is currently restricted to Australia only. Read-only.
      </div>
      <SaveRow saving={saving} onSave={updateSettings} section="shipping" label="Save Shipping Settings" />
    </section>
  );

  const renderEmail = () => (
    <>
      <section className="adm-card pgset-card">
        <CardHead title="Email / SMTP Configuration" />
        <div className="adm-grid2">
          <label className="adm-field">
            <span className="adm-label">SMTP Host</span>
            <input
              className="adm-input"
              value={settings.email?.smtp_host || ""}
              onChange={(e) =>
                handleInputChange("email", "smtp_host", e.target.value)
              }
              placeholder="smtp.example.com"
            />
          </label>
          <label className="adm-field">
            <span className="adm-label">SMTP Port</span>
            <input
              className="adm-input"
              type="number"
              value={settings.email?.smtp_port ?? ""}
              onChange={(e) =>
                handleInputChange(
                  "email",
                  "smtp_port",
                  parseInt(e.target.value) || 587
                )
              }
              placeholder="587"
            />
          </label>
        </div>
        <div className="adm-grid2">
          <label className="adm-field">
            <span className="adm-label">SMTP Username</span>
            <input
              className="adm-input"
              value={settings.email?.smtp_user || ""}
              onChange={(e) =>
                handleInputChange("email", "smtp_user", e.target.value)
              }
              placeholder="Your Postmark API Token"
            />
          </label>
          <SecretField revealedMap={revealed} onToggle={toggleReveal}
            id="smtp_password"
            label="SMTP Password"
            value={settings.email?.smtp_password || ""}
            placeholder="••••••••"
            onChange={(e) =>
              handleInputChange("email", "smtp_password", e.target.value)
            }
          />
        </div>
        <div className="adm-grid2">
          <label className="adm-field">
            <span className="adm-label">Sender Email</span>
            <input
              className="adm-input"
              type="email"
              value={settings.email?.sender_email || ""}
              onChange={(e) =>
                handleInputChange("email", "sender_email", e.target.value)
              }
              placeholder="admin@umoonaopalmine.com.au"
            />
          </label>
          <label className="adm-field">
            <span className="adm-label">Sender Name</span>
            <input
              className="adm-input"
              value={settings.email?.sender_name || ""}
              onChange={(e) =>
                handleInputChange("email", "sender_name", e.target.value)
              }
              placeholder="Umoona Opal Mine"
            />
          </label>
        </div>
        <div className="adm-sec-eyebrow">Admin notification emails</div>
        <div className="pgset-emails">
          {notificationEmails.map((email, index) => (
            <div key={index} className="pgset-emailrow">
              <input
                className="adm-input pgset-emailinput"
                type="email"
                value={email}
                onChange={(e) => updateEmailAt(index, e.target.value)}
              />
              <button
                type="button"
                className="pgset-xbtn"
                onClick={() => removeEmailAddress(index)}
                disabled={notificationEmails.length <= 1}
                title={
                  notificationEmails.length <= 1
                    ? "At least one notification email is required"
                    : "Remove email"
                }
                aria-label="Remove email"
              >
                ✕
              </button>
            </div>
          ))}
          {showAddEmail && (
            <div className="pgset-emailrow">
              <input
                className="adm-input pgset-emailinput"
                type="email"
                value={newEmailAddress}
                onChange={(e) => setNewEmailAddress(e.target.value)}
                placeholder="Enter new admin notification email"
                autoFocus
              />
              <button
                type="button"
                className="adm-btn"
                onClick={confirmAddEmail}
                disabled={!newEmailAddress.includes("@")}
              >
                Add
              </button>
              <button
                type="button"
                className="adm-btn adm-btn--soft"
                onClick={() => {
                  setShowAddEmail(false);
                  setNewEmailAddress("");
                }}
              >
                Cancel
              </button>
            </div>
          )}
        </div>
        {!showAddEmail && (
          <button
            type="button"
            className="adm-btn adm-btn--dashed"
            onClick={() => setShowAddEmail(true)}
          >
            + Add notification email
          </button>
        )}
        <SaveRow saving={saving} onSave={updateSettings} section="email" label="Save Email Settings" />
      </section>

      <section className="adm-card pgset-card">
        <div className="adm-card-head">
          <div className="adm-card-title adm-card-title--lg">
            Email Testing
          </div>
          <span className="adm-card-note">
            Template tests are sent to hello@turbo360.com.au
          </span>
        </div>
        <div className="pgset-testrow">
          <input
            className="adm-input pgset-testinput"
            type="email"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            placeholder="test@example.com"
            aria-label="Basic test email address"
          />
          <button
            type="button"
            className="adm-btn adm-btn--dark"
            onClick={testEmailSettings}
            disabled={testingEmail || !testEmail}
          >
            {testingEmail ? "Sending…" : "✉ Send Basic Test"}
          </button>
        </div>
        <div className="pgset-hint pgset-testhint">
          Send a basic test email to verify SMTP configuration
        </div>
        <div className="pgset-tests">
          {TEMPLATE_TESTS.map((t) => (
            <button
              key={t.endpoint}
              type="button"
              className="adm-btn adm-btn--ghost"
              onClick={() => runTemplateTest(t.endpoint, t.success)}
              disabled={testingEmail}
            >
              {t.label}
            </button>
          ))}
        </div>
      </section>
    </>
  );

  /* --- opening hours --- */

  const setOpeningHours = (patch) =>
    setSettings((prev) => ({
      ...prev,
      opening_hours: { ...prev.opening_hours, ...patch(prev.opening_hours || {}) },
    }));

  const updateWeekday = (day, field, value) =>
    setOpeningHours((oh) => {
      const weekly = weeklyOf(oh).map((d) => (d.day === day ? { ...d, [field]: value } : d));
      return day === 1 && field === "closed"
        ? { weekly, closed_on_tuesdays: value }
        : { weekly };
    });

  const updateSpecialDay = (index, field, value) =>
    setOpeningHours((oh) => {
      const list = [...(oh.special_days || [])];
      list[index] = { ...list[index], [field]: value };
      return { special_days: list };
    });

  const renderHours = () => {
    const oh = settings.opening_hours || {};
    const weekly = weeklyOf(oh);
    const specialDays = oh.special_days || [];
    return (
    <section className="adm-card pgset-card">
      <CardHead title="Opening Hours" />
      <label className="adm-switch pgset-switchrow">
        <input
          type="checkbox"
          checked={weekly[1].closed || false}
          onChange={(e) => updateWeekday(1, "closed", e.target.checked)}
        />
        <span className="adm-switch-track" aria-hidden="true" />
        <span>Closed on Tuesdays (seasonal)</span>
      </label>
      <label className="adm-switch pgset-switchrow">
        <input
          type="checkbox"
          checked={oh.holiday_hours_notice || false}
          onChange={(e) =>
            setOpeningHours(() => ({ holiday_hours_notice: e.target.checked }))
          }
        />
        <span className="adm-switch-track" aria-hidden="true" />
        <span>Show Holiday Hours Notice on website</span>
      </label>

      <div className="adm-sec-eyebrow">Weekly hours</div>
      {weekly.map((d) => (
        <div key={d.day} className="pgset-hoursrow">
          <span className="pgset-hoursday">{WEEKDAY_NAMES[d.day]}</span>
          <label className="pgset-on">
            <input
              type="checkbox"
              className="adm-check"
              checked={!d.closed}
              onChange={(e) => updateWeekday(d.day, "closed", !e.target.checked)}
            />{" "}
            Open
          </label>
          {d.closed ? (
            <span className="pgset-hoursclosed">Closed all day</span>
          ) : (
            <>
              <input
                className="pgset-cell"
                type="time"
                step={900}
                value={d.open || ""}
                onChange={(e) => updateWeekday(d.day, "open", e.target.value)}
                aria-label={`${WEEKDAY_NAMES[d.day]} opening time`}
              />
              <input
                className="pgset-cell"
                type="time"
                step={900}
                value={d.close || ""}
                onChange={(e) => updateWeekday(d.day, "close", e.target.value)}
                aria-label={`${WEEKDAY_NAMES[d.day]} closing time`}
              />
            </>
          )}
        </div>
      ))}

      <div className="adm-sec-eyebrow">Special days</div>
      <p className="pgset-hint">
        Public holidays or one-off days with different hours. Shown on the
        website as "Upcoming changes" in the 60 days before.
      </p>
      {specialDays.map((d, index) => (
        <div key={index} className="pgset-specialrow">
          <input
            className="pgset-cell"
            type="date"
            value={d.date || ""}
            onChange={(e) => updateSpecialDay(index, "date", e.target.value)}
            aria-label="Date"
          />
          <input
            className="pgset-cell"
            placeholder="Label (e.g., Christmas Day)"
            value={d.label || ""}
            onChange={(e) => updateSpecialDay(index, "label", e.target.value)}
            aria-label="Label"
          />
          <label className="pgset-on">
            <input
              type="checkbox"
              className="adm-check"
              checked={!!d.closed}
              onChange={(e) => updateSpecialDay(index, "closed", e.target.checked)}
            />{" "}
            Closed
          </label>
          {d.closed ? (
            <span className="pgset-hoursclosed">Closed all day</span>
          ) : (
            <>
              <input
                className="pgset-cell"
                type="time"
                step={900}
                value={d.open || ""}
                onChange={(e) => updateSpecialDay(index, "open", e.target.value)}
                aria-label="Opening time"
              />
              <input
                className="pgset-cell"
                type="time"
                step={900}
                value={d.close || ""}
                onChange={(e) => updateSpecialDay(index, "close", e.target.value)}
                aria-label="Closing time"
              />
            </>
          )}
          <button
            type="button"
            className="pgset-xbtn"
            onClick={() =>
              setOpeningHours((cur) => ({
                special_days: (cur.special_days || []).filter((_, i) => i !== index),
              }))
            }
            title="Remove special day"
            aria-label="Remove special day"
          >
            ✕
          </button>
        </div>
      ))}
      {specialDays.length === 0 && (
        <div className="pgset-emptypkg">No special days set.</div>
      )}
      <button
        type="button"
        className="adm-btn adm-btn--dashed pgset-mb24"
        onClick={() =>
          setOpeningHours((cur) => ({
            special_days: [...(cur.special_days || []), { ...EMPTY_SPECIAL_DAY }],
          }))
        }
      >
        + Add special day
      </button>

      <div className="adm-sec-eyebrow">Live preview</div>
      <div className="pgset-preview">
        <div className="pgset-preview-title">Umoona Opal Mine &amp; Museum</div>
        {liveHours ? (
          <>
            {(liveHours.summary || []).map((line) =>
              /^Closed /.test(line) ? (
                <div key={line} className="pgset-preview-closed">{line}</div>
              ) : (
                <div key={line} className="pgset-preview-open">{line}</div>
              )
            )}
            {(liveHours.special_days || []).length > 0 && (
              <div className="pgset-preview-holiday">
                <div className="pgset-preview-holiday-title">Upcoming changes</div>
                {liveHours.special_days.map((d) => (
                  <div key={d.date}>{formatSpecialDay(d)}</div>
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="pgset-preview-open">Loading…</div>
        )}
        {oh.holiday_hours_notice && (
          <div className="pgset-preview-holiday">
            <div className="pgset-preview-holiday-title">Seasonal Closure</div>
            Umoona Opal Mine is seasonally closed for entire month of February.
            We will re-open to the public on 2 March 2026.
          </div>
        )}
        <div className="pgset-preview-note">As shown on the website · updates after you save</div>
      </div>
      <SaveRow saving={saving} onSave={updateSettings} section="opening_hours" label="Save Opening Hours" />
    </section>
    );
  };

  const renderTours = () => (
    <section className="adm-card pgset-card">
      <CardHead title="Tour Pricing & Schedule" />
      <div className="adm-sec-eyebrow">Individual pricing</div>
      <div className="adm-grid2">
        <label className="adm-field">
          <span className="adm-label">Adult Price (AUD)</span>
          <input
            className="adm-input"
            type="number"
            step="0.01"
            value={settings.tour_pricing?.adult_price || 16.5}
            onChange={(e) =>
              handleInputChange(
                "tour_pricing",
                "adult_price",
                parseFloat(e.target.value)
              )
            }
            placeholder="16.50"
          />
        </label>
        <label className="adm-field">
          <span className="adm-label">Child Price (AUD)</span>
          <input
            className="adm-input"
            type="number"
            step="0.01"
            value={settings.tour_pricing?.child_price || 9.5}
            onChange={(e) =>
              handleInputChange(
                "tour_pricing",
                "child_price",
                parseFloat(e.target.value)
              )
            }
            placeholder="9.50"
          />
        </label>
      </div>

      <div className="adm-sec-eyebrow">Family packages</div>
      {(settings.tour_pricing?.family_packages || []).map((pkg, index) => (
        <div key={index} className="pgset-pkgrow">
          <input
            className="pgset-cell"
            value={pkg.name || ""}
            onChange={(e) =>
              updatePackageField("family_packages", index, "name", e.target.value)
            }
            aria-label="Package name"
          />
          <input
            className="pgset-cell"
            type="number"
            step="0.01"
            value={pkg.price ?? ""}
            onChange={(e) =>
              updatePackageField(
                "family_packages",
                index,
                "price",
                parseFloat(e.target.value) || 0
              )
            }
            aria-label="Price (AUD)"
          />
          <input
            className="pgset-cell"
            value={pkg.description || ""}
            onChange={(e) =>
              updatePackageField(
                "family_packages",
                index,
                "description",
                e.target.value
              )
            }
            aria-label="Description"
          />
          <input
            className="pgset-cell"
            type="number"
            min={3}
            max={8}
            value={pkg.guest_count ?? ""}
            onChange={(e) =>
              updatePackageField(
                "family_packages",
                index,
                "guest_count",
                parseInt(e.target.value) || 0
              )
            }
            aria-label="People (3–8)"
            title="People (3–8)"
          />
          <label className="pgset-on">
            <input
              type="checkbox"
              className="adm-check"
              checked={pkg.enabled !== false}
              onChange={(e) =>
                updatePackageField(
                  "family_packages",
                  index,
                  "enabled",
                  e.target.checked
                )
              }
            />{" "}
            On
          </label>
          <button
            type="button"
            className="pgset-xbtn"
            onClick={() => removePackage("family_packages", index)}
            title="Remove package"
            aria-label="Remove package"
          >
            ✕
          </button>
        </div>
      ))}
      {(!settings.tour_pricing?.family_packages ||
        settings.tour_pricing.family_packages.length === 0) && (
        <div className="pgset-emptypkg">
          No family packages configured. Click "Add family package" to create
          one.
        </div>
      )}
      {showAddFamilyPackage ? (
        <div className="pgset-addbox pgset-mb24">
          <div className="pgset-addbox-title">Add New Family Package</div>
          <div className="pgset-addgrid">
            <input
              className="adm-input adm-input--sm"
              placeholder="Package Name (e.g., Family of 4)"
              value={newFamilyPackage.name}
              onChange={(e) =>
                setNewFamilyPackage((prev) => ({
                  ...prev,
                  name: e.target.value,
                }))
              }
            />
            <input
              className="adm-input adm-input--sm"
              type="number"
              step="0.01"
              placeholder="Price"
              value={newFamilyPackage.price}
              onChange={(e) =>
                setNewFamilyPackage((prev) => ({
                  ...prev,
                  price: parseFloat(e.target.value) || 0,
                }))
              }
            />
            <input
              className="adm-input adm-input--sm"
              placeholder="Description (e.g., 2 Adults + 2 Children under 16)"
              value={newFamilyPackage.description}
              onChange={(e) =>
                setNewFamilyPackage((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
            />
            <input
              className="adm-input adm-input--sm"
              type="number"
              placeholder="People, 3–8 (e.g., 4)"
              min={3}
              max={8}
              value={newFamilyPackage.guest_count || ""}
              onChange={(e) =>
                setNewFamilyPackage((prev) => ({
                  ...prev,
                  guest_count: parseInt(e.target.value) || 0,
                }))
              }
            />
          </div>
          <div className="pgset-actions">
            <button
              type="button"
              className="adm-btn"
              onClick={addFamilyPackage}
              disabled={
                !newFamilyPackage.name ||
                !newFamilyPackage.price ||
                !newFamilyPackage.description ||
                !(newFamilyPackage.guest_count >= 3 && newFamilyPackage.guest_count <= 8)
              }
            >
              Add Package
            </button>
            <button
              type="button"
              className="adm-btn adm-btn--soft"
              onClick={() => {
                setShowAddFamilyPackage(false);
                setNewFamilyPackage({
                  name: "",
                  price: "",
                  description: "",
                  guest_count: "",
                  enabled: true,
                });
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          className="adm-btn adm-btn--dashed pgset-mb24"
          onClick={() => setShowAddFamilyPackage(true)}
        >
          + Add family package
        </button>
      )}

      <div className="adm-sec-eyebrow">Custom packages</div>
      {(settings.tour_pricing?.custom_packages || []).map((pkg, index) => (
        <div key={index} className="pgset-pkgrow pgset-pkgrow--custom">
          <input
            className="pgset-cell"
            value={pkg.name || ""}
            onChange={(e) =>
              updatePackageField("custom_packages", index, "name", e.target.value)
            }
            aria-label="Package name"
          />
          <input
            className="pgset-cell"
            type="number"
            step="0.01"
            value={pkg.price ?? ""}
            onChange={(e) =>
              updatePackageField(
                "custom_packages",
                index,
                "price",
                parseFloat(e.target.value) || 0
              )
            }
            aria-label="Price (AUD)"
          />
          <input
            className="pgset-cell"
            value={pkg.description || ""}
            onChange={(e) =>
              updatePackageField(
                "custom_packages",
                index,
                "description",
                e.target.value
              )
            }
            aria-label="Description"
          />
          <input
            className="pgset-cell"
            type="number"
            min={1}
            max={100}
            value={pkg.guest_count ?? ""}
            onChange={(e) =>
              updatePackageField(
                "custom_packages",
                index,
                "guest_count",
                parseInt(e.target.value) || 0
              )
            }
            aria-label="People (1–100)"
            title="People (1–100)"
          />
          <label className="pgset-on">
            <input
              type="checkbox"
              className="adm-check"
              checked={pkg.enabled !== false}
              onChange={(e) =>
                updatePackageField(
                  "custom_packages",
                  index,
                  "enabled",
                  e.target.checked
                )
              }
            />{" "}
            On
          </label>
          <button
            type="button"
            className="pgset-xbtn"
            onClick={() => removePackage("custom_packages", index)}
            title="Remove package"
            aria-label="Remove package"
          >
            ✕
          </button>
        </div>
      ))}
      {showAddPackage ? (
        <div className="pgset-addbox pgset-mb24">
          <div className="pgset-addbox-title">Add New Package</div>
          <div className="pgset-addgrid">
            <input
              className="adm-input adm-input--sm"
              placeholder="Package Name (e.g., Senior)"
              value={newPackage.name}
              onChange={(e) =>
                setNewPackage((prev) => ({ ...prev, name: e.target.value }))
              }
            />
            <input
              className="adm-input adm-input--sm"
              type="number"
              step="0.01"
              placeholder="Price"
              value={newPackage.price}
              onChange={(e) =>
                setNewPackage((prev) => ({
                  ...prev,
                  price: parseFloat(e.target.value) || 0,
                }))
              }
            />
            <input
              className="adm-input adm-input--sm"
              placeholder="Description"
              value={newPackage.description}
              onChange={(e) =>
                setNewPackage((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
            />
            <input
              className="adm-input adm-input--sm"
              type="number"
              min={1}
              max={100}
              placeholder="People (e.g., 2)"
              value={newPackage.guest_count || ""}
              onChange={(e) =>
                setNewPackage((prev) => ({
                  ...prev,
                  guest_count: parseInt(e.target.value) || 0,
                }))
              }
            />
          </div>
          <div className="pgset-actions">
            <button
              type="button"
              className="adm-btn"
              onClick={addCustomPackage}
              disabled={
                !newPackage.name ||
                !newPackage.price ||
                !newPackage.description ||
                !(newPackage.guest_count >= 1 && newPackage.guest_count <= 100)
              }
            >
              Add Package
            </button>
            <button
              type="button"
              className="adm-btn adm-btn--soft"
              onClick={() => {
                setShowAddPackage(false);
                setNewPackage({
                  name: "",
                  price: "",
                  description: "",
                  guest_count: "",
                  enabled: true,
                });
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          className="adm-btn adm-btn--dashed pgset-mb24"
          onClick={() => setShowAddPackage(true)}
        >
          + Add custom package
        </button>
      )}

      <div className="adm-sec-eyebrow">Tour schedule</div>
      <label className="adm-switch pgset-switchrow">
        <input
          type="checkbox"
          checked={restricted}
          onChange={(e) =>
            handleInputChange(
              "tour_schedule",
              "restrict_monday_friday",
              e.target.checked
            )
          }
        />
        <span className="adm-switch-track" aria-hidden="true" />
        <span>Restrict Monday &amp; Friday to 4pm tour only</span>
      </label>

      {restricted ? (
        <>
          <div className="adm-grid2">
            <label className="adm-field">
              <span className="adm-label">Restriction Start Date</span>
              <input
                className="adm-input"
                type="date"
                value={settings.tour_schedule?.restriction_start_date || ""}
                onChange={(e) =>
                  handleInputChange(
                    "tour_schedule",
                    "restriction_start_date",
                    e.target.value || null
                  )
                }
              />
            </label>
            <label className="adm-field">
              <span className="adm-label">Restriction End Date</span>
              <input
                className="adm-input"
                type="date"
                value={settings.tour_schedule?.restriction_end_date || ""}
                onChange={(e) =>
                  handleInputChange(
                    "tour_schedule",
                    "restriction_end_date",
                    e.target.value || null
                  )
                }
              />
            </label>
          </div>
          {settings.tour_schedule?.restriction_start_date ? (
            <div className="adm-note adm-note--amber">
              <strong>Active Period:</strong> Restriction applies from{" "}
              <strong>
                {fmtLongDate(settings.tour_schedule.restriction_start_date)}
              </strong>
              {settings.tour_schedule?.restriction_end_date ? (
                <>
                  {" "}
                  until{" "}
                  <strong>
                    {fmtLongDate(settings.tour_schedule.restriction_end_date)}
                  </strong>
                </>
              ) : (
                <> onwards (indefinitely)</>
              )}
            </div>
          ) : (
            <div className="adm-note">
              <strong>Note:</strong> No start date set. Restriction is active
              immediately for all dates.
            </div>
          )}

          <div className="adm-sec-eyebrow">Override conditions</div>
          <div className="pgset-hint pgset-overridehint">
            Temporarily re-open additional tour times on restricted
            Mondays/Fridays during specific date ranges (e.g. school holidays,
            festivals).
          </div>
          {(settings.tour_schedule?.overrides || []).map((override, index) => (
            <div
              key={index}
              className={
                "pgset-override" + (override.enabled ? "" : " is-off")
              }
            >
              <div className="pgset-override-head">
                <input
                  className="pgset-override-label"
                  placeholder={`Override #${index + 1} — e.g. School Holidays April`}
                  value={override.label || ""}
                  onChange={(e) =>
                    updateOverrideField(index, "label", e.target.value)
                  }
                />
                <label className="pgset-on">
                  <input
                    type="checkbox"
                    className="adm-check"
                    checked={!!override.enabled}
                    onChange={() => handleToggleOverride(index)}
                  />{" "}
                  Enabled
                </label>
                <button
                  type="button"
                  className="pgset-xbtn"
                  onClick={() => handleRemoveOverride(index)}
                  title="Remove override"
                  aria-label="Remove override"
                >
                  ✕
                </button>
              </div>
              <div className="pgset-override-dates">
                <input
                  type="date"
                  value={override.start_date || ""}
                  onChange={(e) =>
                    updateOverrideField(index, "start_date", e.target.value)
                  }
                  aria-label="Override start date"
                />
                <input
                  type="date"
                  value={override.end_date || ""}
                  onChange={(e) =>
                    updateOverrideField(index, "end_date", e.target.value)
                  }
                  aria-label="Override end date"
                />
              </div>
              <div className="pgset-override-checks">
                <label>
                  <input
                    type="checkbox"
                    className="adm-check"
                    checked={!!override.apply_monday}
                    onChange={(e) =>
                      updateOverrideField(
                        index,
                        "apply_monday",
                        e.target.checked
                      )
                    }
                  />{" "}
                  Apply Monday
                </label>
                <label>
                  <input
                    type="checkbox"
                    className="adm-check"
                    checked={!!override.apply_friday}
                    onChange={(e) =>
                      updateOverrideField(
                        index,
                        "apply_friday",
                        e.target.checked
                      )
                    }
                  />{" "}
                  Apply Friday
                </label>
                <label>
                  <input
                    type="checkbox"
                    className="adm-check"
                    checked={!!override.allow_morning}
                    onChange={(e) =>
                      updateOverrideField(
                        index,
                        "allow_morning",
                        e.target.checked
                      )
                    }
                  />{" "}
                  Allow 10am tour
                </label>
                <label>
                  <input
                    type="checkbox"
                    className="adm-check"
                    checked={!!override.allow_afternoon}
                    onChange={(e) =>
                      updateOverrideField(
                        index,
                        "allow_afternoon",
                        e.target.checked
                      )
                    }
                  />{" "}
                  Allow 2pm tour
                </label>
              </div>
            </div>
          ))}
          {showAddOverride ? (
            <div className="pgset-addbox">
              <div className="pgset-addbox-title">Add Override Condition</div>
              <label className="adm-field">
                <span className="adm-label">Label (optional)</span>
                <input
                  className="adm-input"
                  placeholder="e.g. School Holidays April"
                  value={newOverride.label}
                  onChange={(e) =>
                    setNewOverride((prev) => ({
                      ...prev,
                      label: e.target.value,
                    }))
                  }
                />
              </label>
              <div className="adm-grid2">
                <label className="adm-field">
                  <span className="adm-label">Start Date *</span>
                  <input
                    className="adm-input"
                    type="date"
                    value={newOverride.start_date}
                    onChange={(e) =>
                      setNewOverride((prev) => ({
                        ...prev,
                        start_date: e.target.value,
                      }))
                    }
                  />
                </label>
                <label className="adm-field">
                  <span className="adm-label">End Date *</span>
                  <input
                    className="adm-input"
                    type="date"
                    value={newOverride.end_date}
                    onChange={(e) =>
                      setNewOverride((prev) => ({
                        ...prev,
                        end_date: e.target.value,
                      }))
                    }
                  />
                </label>
              </div>
              <div className="pgset-override-checks pgset-mb12">
                <label>
                  <input
                    type="checkbox"
                    className="adm-check"
                    checked={newOverride.apply_monday}
                    onChange={(e) =>
                      setNewOverride((prev) => ({
                        ...prev,
                        apply_monday: e.target.checked,
                      }))
                    }
                  />{" "}
                  Apply Monday
                </label>
                <label>
                  <input
                    type="checkbox"
                    className="adm-check"
                    checked={newOverride.apply_friday}
                    onChange={(e) =>
                      setNewOverride((prev) => ({
                        ...prev,
                        apply_friday: e.target.checked,
                      }))
                    }
                  />{" "}
                  Apply Friday
                </label>
                <label>
                  <input
                    type="checkbox"
                    className="adm-check"
                    checked={newOverride.allow_morning}
                    onChange={(e) =>
                      setNewOverride((prev) => ({
                        ...prev,
                        allow_morning: e.target.checked,
                      }))
                    }
                  />{" "}
                  Allow 10am tour
                </label>
                <label>
                  <input
                    type="checkbox"
                    className="adm-check"
                    checked={newOverride.allow_afternoon}
                    onChange={(e) =>
                      setNewOverride((prev) => ({
                        ...prev,
                        allow_afternoon: e.target.checked,
                      }))
                    }
                  />{" "}
                  Allow 2pm tour
                </label>
              </div>
              <div className="pgset-hint pgset-mb12">
                4:00 PM is always available on restricted days
              </div>
              <div className="pgset-actions">
                <button
                  type="button"
                  className="adm-btn"
                  onClick={handleAddOverride}
                  disabled={!newOverride.start_date || !newOverride.end_date}
                >
                  Add Override
                </button>
                <button
                  type="button"
                  className="adm-btn adm-btn--soft"
                  onClick={() => setShowAddOverride(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              className="adm-btn adm-btn--dashed"
              onClick={() => setShowAddOverride(true)}
            >
              + Add override condition
            </button>
          )}
        </>
      ) : (
        <div className="adm-note adm-note--teal">
          ✓ All tour times enabled for every day of the week
        </div>
      )}

      <SaveRow saving={saving} onSave={updateSettings} section="tour_pricing" label="Save Tour Settings" />
    </section>
  );

  const renderCoupons = () => (
    <section className="adm-card pgset-card">
      <div className="adm-card-head">
        <div className="adm-card-title adm-card-title--lg">Discount Coupons</div>
        <span className="adm-card-note">Saves immediately</span>
      </div>
      <div className="pgset-hint pgset-mb12">
        Customers enter a code on the Book Tour page to get a percentage off
        their tour tickets.
        Codes aren't case-sensitive.
      </div>
      {couponsLoading ? (
        <div className="adm-loading">
          <div className="adm-spinner" />
          Loading coupons…
        </div>
      ) : coupons.length === 0 ? (
        <div className="pgset-emptypkg">
          No coupons yet. Click "Add coupon" to create one.
        </div>
      ) : (
        coupons.map((coupon) => (
          <div key={coupon.id} className="pgset-couponrow">
            <span className="pgset-couponcode">{coupon.code}</span>
            <span className="adm-chip adm-chip--teal">
              {coupon.discount_percent}% off
            </span>
            <span className="pgset-coupondesc">{coupon.description || "—"}</span>
            <span className="pgset-couponuses">
              Used {coupon.times_used} {coupon.times_used === 1 ? "time" : "times"}
            </span>
            <button
              type="button"
              className="pgset-xbtn"
              onClick={() => setDeletingCoupon(coupon)}
              title="Delete coupon"
              aria-label={`Delete coupon ${coupon.code}`}
            >
              ✕
            </button>
          </div>
        ))
      )}
      {showAddCoupon ? (
        <div className="pgset-addbox">
          <div className="pgset-addbox-title">Add New Coupon</div>
          <div className="pgset-addgrid pgset-addgrid--coupon">
            <input
              className="adm-input adm-input--sm pgset-codeinput"
              placeholder="Code (e.g., APOD26)"
              maxLength={30}
              value={newCoupon.code}
              onChange={(e) =>
                setNewCoupon((prev) => ({
                  ...prev,
                  code: e.target.value.toUpperCase().replace(/\s/g, ""),
                }))
              }
            />
            <input
              className="adm-input adm-input--sm"
              type="number"
              min="1"
              max="99"
              step="1"
              placeholder="% off (e.g., 20)"
              value={newCoupon.discount_percent}
              onChange={(e) =>
                setNewCoupon((prev) => ({
                  ...prev,
                  discount_percent: e.target.value,
                }))
              }
            />
            <input
              className="adm-input adm-input--sm"
              placeholder="Note (optional)"
              value={newCoupon.description}
              onChange={(e) =>
                setNewCoupon((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
            />
          </div>
          <div className="pgset-actions">
            <button
              type="button"
              className="adm-btn"
              onClick={addCoupon}
              disabled={
                couponBusy ||
                !newCoupon.code.trim() ||
                !parseFloat(newCoupon.discount_percent)
              }
            >
              {couponBusy ? "Adding…" : "Add Coupon"}
            </button>
            <button
              type="button"
              className="adm-btn adm-btn--soft"
              onClick={() => {
                setShowAddCoupon(false);
                setNewCoupon(EMPTY_COUPON);
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          className="adm-btn adm-btn--dashed"
          onClick={() => setShowAddCoupon(true)}
        >
          + Add coupon
        </button>
      )}
      <ConfirmModal
        open={!!deletingCoupon}
        title={`Delete coupon ${deletingCoupon ? deletingCoupon.code : ""}?`}
        body="Customers will no longer be able to use this code. Bookings that already used it keep their discount."
        confirmLabel="Delete Coupon"
        danger
        busy={couponBusy}
        onConfirm={confirmDeleteCoupon}
        onCancel={() => setDeletingCoupon(null)}
      />
    </section>
  );

  const renderStripe = () => (
    <section className="adm-card pgset-card">
      <CardHead title="Stripe Payment Configuration" />
      <div
        className={
          "pgset-stripemode" + (useLive ? " pgset-stripemode--live" : "")
        }
      >
        <label className="adm-switch adm-switch--danger pgset-modeswitch">
          <input
            type="checkbox"
            checked={useLive}
            onChange={(e) =>
              setSettings((prev) => ({
                ...prev,
                stripe: {
                  ...prev.stripe,
                  use_live_keys: e.target.checked,
                },
              }))
            }
          />
          <span className="adm-switch-track" aria-hidden="true" />
          <span>Use LIVE keys</span>
        </label>
        <span
          className={
            "pgset-modepill" + (useLive ? " pgset-modepill--live" : "")
          }
        >
          {useLive ? "CURRENTLY IN LIVE MODE" : "CURRENTLY IN TEST MODE"}
        </span>
        <span
          className={
            "pgset-modenote" + (useLive ? " pgset-modenote--live" : "")
          }
        >
          {useLive
            ? "Using LIVE Stripe keys for real transactions. Customers will be charged!"
            : "No real payments are processed in test mode."}
        </span>
      </div>

      <div className="adm-sec-eyebrow">Test keys</div>
      <SecretField revealedMap={revealed} onToggle={toggleReveal}
        id="test_api_key"
        label="Test API Key"
        value={settings.stripe?.test_api_key || ""}
        placeholder="sk_test_…"
        onChange={(e) =>
          setSettings((prev) => ({
            ...prev,
            stripe: { ...prev.stripe, test_api_key: e.target.value },
          }))
        }
      />
      <SecretField revealedMap={revealed} onToggle={toggleReveal}
        id="test_publishable_key"
        label="Test Publishable Key"
        value={settings.stripe?.test_publishable_key || ""}
        placeholder="pk_test_…"
        onChange={(e) =>
          setSettings((prev) => ({
            ...prev,
            stripe: { ...prev.stripe, test_publishable_key: e.target.value },
          }))
        }
      />
      <SecretField revealedMap={revealed} onToggle={toggleReveal}
        id="test_webhook_secret"
        label="Test Webhook Secret"
        value={settings.stripe?.test_webhook_secret || ""}
        placeholder="whsec_…"
        onChange={(e) =>
          setSettings((prev) => ({
            ...prev,
            stripe: { ...prev.stripe, test_webhook_secret: e.target.value },
          }))
        }
      />

      <div className="adm-gap" />
      <div className="adm-sec-eyebrow">Live keys</div>
      <div className="pgset-warn">
        ⚠ Live keys process real payments. Handle with care.
      </div>
      <SecretField revealedMap={revealed} onToggle={toggleReveal}
        id="live_api_key"
        label="Live API Key"
        value={settings.stripe?.live_api_key || ""}
        placeholder="sk_live_…"
        onChange={(e) =>
          setSettings((prev) => ({
            ...prev,
            stripe: { ...prev.stripe, live_api_key: e.target.value },
          }))
        }
      />
      <SecretField revealedMap={revealed} onToggle={toggleReveal}
        id="live_publishable_key"
        label="Live Publishable Key"
        value={settings.stripe?.live_publishable_key || ""}
        placeholder="pk_live_…"
        onChange={(e) =>
          setSettings((prev) => ({
            ...prev,
            stripe: { ...prev.stripe, live_publishable_key: e.target.value },
          }))
        }
      />
      <SecretField revealedMap={revealed} onToggle={toggleReveal}
        id="live_webhook_secret"
        label="Live Webhook Secret"
        value={settings.stripe?.live_webhook_secret || ""}
        placeholder="whsec_…"
        onChange={(e) =>
          setSettings((prev) => ({
            ...prev,
            stripe: { ...prev.stripe, live_webhook_secret: e.target.value },
          }))
        }
      />

      <SaveRow saving={saving} onSave={updateSettings} section="stripe" label="Save Stripe Settings" />
    </section>
  );

  const renderFeatures = () => (
    <section className="adm-card pgset-card">
      <CardHead title="Feature Toggles" />
      <div className="adm-featrow">
        <div>
          <div className="adm-featrow-title">Enable Tour Booking</div>
          <div className="adm-featrow-sub">
            Allow customers to book underground tours online.
          </div>
        </div>
        <label className="adm-switch">
          <input
            type="checkbox"
            checked={settings.features?.tour_booking_enabled !== false}
            onChange={(e) =>
              setSettings((prev) => ({
                ...prev,
                features: {
                  ...prev.features,
                  tour_booking_enabled: e.target.checked,
                },
              }))
            }
            aria-label="Enable Tour Booking"
          />
          <span className="adm-switch-track" aria-hidden="true" />
        </label>
      </div>
      <div className="adm-featrow adm-featrow--danger">
        <div>
          <div className="adm-featrow-title">Store Maintenance Mode</div>
          <div className="adm-featrow-sub">
            Takes the online store offline for customers. Use with caution.
          </div>
        </div>
        <label className="adm-switch adm-switch--danger">
          <input
            type="checkbox"
            checked={settings.features?.store_maintenance_mode === true}
            onChange={(e) =>
              setSettings((prev) => ({
                ...prev,
                features: {
                  ...prev.features,
                  store_maintenance_mode: e.target.checked,
                },
              }))
            }
            aria-label="Store Maintenance Mode"
          />
          <span className="adm-switch-track" aria-hidden="true" />
        </label>
      </div>
      <div className="adm-note adm-note--amber">
        <div className="adm-note-eyebrow">Important</div>
        Disabling tour booking will immediately hide all tour booking
        functionality from customers. Existing bookings will not be affected.
      </div>
      <SaveRow saving={saving} onSave={updateSettings} section="features" label="Save Features" />
    </section>
  );

  const renderGeneral = () => (
    <section className="adm-card pgset-card">
      <CardHead title="General / Store Settings" />
      <div className="adm-grid2">
        <label className="adm-field">
          <span className="adm-label">Shop Name</span>
          <input
            className="adm-input"
            value={settings.shop_name || ""}
            onChange={(e) =>
              handleInputChange(null, "shop_name", e.target.value)
            }
            placeholder="Umoona Opal Mine"
          />
        </label>
        <label className="adm-field">
          <span className="adm-label">Shop URL</span>
          <input
            className="adm-input"
            value={settings.shop_url || ""}
            onChange={(e) =>
              handleInputChange(null, "shop_url", e.target.value)
            }
            placeholder="https://umoonaopalmine.com.au"
          />
        </label>
      </div>
      <div className="adm-grid2">
        <label className="adm-field">
          <span className="adm-label">Currency</span>
          <input
            className="adm-input"
            value={settings.currency || ""}
            onChange={(e) =>
              handleInputChange(null, "currency", e.target.value)
            }
            placeholder="AUD"
          />
        </label>
        <label className="adm-field">
          <span className="adm-label">Currency Symbol</span>
          <input
            className="adm-input"
            value={settings.currency_symbol || ""}
            onChange={(e) =>
              handleInputChange(null, "currency_symbol", e.target.value)
            }
            placeholder="$"
          />
        </label>
      </div>
      <div className="adm-grid2">
        <label className="adm-field">
          <span className="adm-label">Tax Rate (GST, decimal)</span>
          <input
            className="adm-input"
            type="number"
            step="0.01"
            min="0"
            max="1"
            value={settings.tax_rate ?? ""}
            onChange={(e) =>
              handleInputChange(
                null,
                "tax_rate",
                parseFloat(e.target.value) || 0
              )
            }
            placeholder="0.10 = 10%"
          />
        </label>
        <div className="pgset-alignend">
          <label className="adm-switch">
            <input
              type="checkbox"
              checked={settings.tax_inclusive || false}
              onChange={(e) =>
                handleInputChange(null, "tax_inclusive", e.target.checked)
              }
            />
            <span className="adm-switch-track" aria-hidden="true" />
            <span>Prices include tax (GST inclusive)</span>
          </label>
        </div>
      </div>
      <SaveRow saving={saving} onSave={updateSettings} section={null} label="Save General Settings" />
    </section>
  );

  /* ---------------- page ---------------- */

  return (
    <AdminShell
      active="settings"
      pageClass="pg-admin-settings"
      eyebrow="System"
      title="System Settings"
    >
      {loading ? (
        <div className="adm-loading">
          <div className="adm-spinner" />
          Loading settings…
        </div>
      ) : (
        <>
          <div className="adm-tabs" role="tablist" aria-label="Settings tabs">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={activeTab === tab.key}
                className={
                  "adm-tab" + (activeTab === tab.key ? " is-active" : "")
                }
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <div className="pgset-wrap">
            {activeTab === "shipping" && renderShipping()}
            {activeTab === "email" && renderEmail()}
            {activeTab === "hours" && renderHours()}
            {activeTab === "tours" && (
              <>
                {renderTours()}
                {renderCoupons()}
              </>
            )}
            {activeTab === "stripe" && renderStripe()}
            {activeTab === "features" && renderFeatures()}
            {activeTab === "general" && renderGeneral()}
          </div>
        </>
      )}
    </AdminShell>
  );
};

export default RAdminSettings;
